<template>
  <ModalMain
    :id="modalId"
    :title="title"
    :ok-title="saveButtonTitle"
    :loading="isLoading"
    delete-disabled
    @hidden="onHidden"
    @ok="onSave"
    @cancel="onCancel"
    @remove="$emit('onRemove', formData.id)"
  >
    <b-form @submit.prevent="onSave">
      <InputString
        class="name"
        :label="$t('label.name')"
        size="lg"
        v-model="$v.form.name.$model"
        :vuelidate="$v.form.name"
      />
      <GalleryPhotos
        :photos="form.files"
        :is-loading="isUploadingPhoto"
        :vuelidate="$v.form.files"
        @change="onChangePhoto"
        @remove="onDeletePhoto"
      />
    </b-form>
  </ModalMain>
</template>

<script>
import { isNil } from 'lodash';
import { mapGetters } from 'vuex';
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import { GALLERY_CREATE, GALLERY_UPDATE } from '@/store/actions/gallery';
import { FILE_DELETE, FILE_UPLOAD } from '@/store/actions/file';
import editModalMixin from '@/mixins/editModalMixin';
import ModalMain from '@/components/ui/ModalMain';
import InputString from '@/components/ui/input/InputString';
import GalleryPhotos from '@/components/manage/galleries/GalleryPhotos';

const defaultValuesForForm = () => ({
  name: '',
  files: [],
});

export default {
  mixins: [validationMixin, editModalMixin],

  props: {
    formData: Object,
  },

  data() {
    return {
      form: defaultValuesForForm(),
    };
  },

  validations: {
    form: {
      name: {
        required,
      },
      files: {
        required,
      },
    },
  },

  watch: {
    formData() {
      this.updateForm();
    },
  },

  computed: {
    ...mapGetters(['galleryStatus', 'getFileUpload']),
    isLoading() {
      return this.galleryStatus === 'loading';
    },
    title() {
      return isNil(this.formData) ? this.$t('gallery.newGallery') : this.$t('gallery.editGallery');
    },
    saveButtonTitle() {
      return isNil(this.formData) ? this.$t('text.addButton') : this.$t('text.updateButton');
    },
    isUploadingPhoto() {
      return this.getFileUpload.status === 'loading';
    },
  },

  methods: {
    updateForm() {
      this.form = this.formData
        ? {
            name: this.formData.name,
            files: [...this.formData.files],
          }
        : defaultValuesForForm();
      this.$v.$reset();
    },
    resetForm() {
      this.form = defaultValuesForForm();
      this.$v.$reset();
    },
    onHidden() {
      this.$emit('onHidden');
      this.resetForm();
    },
    onCancel() {
      this.hideEditModal();
    },
    async onSave() {
      if (this.$v) {
        this.$v.form.$touch();
        if (this.$v.form.$anyError) {
          return;
        }
      }

      const data = this.form;

      if (isNil(this.formData)) {
        await this.$store.dispatch(GALLERY_CREATE, data);
      } else {
        const payload = {
          id: this.formData.id,
          data,
        };
        await this.$store.dispatch(GALLERY_UPDATE, payload);
      }
      if (this.galleryStatus === 'success') {
        this.hideEditModal();
      }
    },
    async onChangePhoto(e) {
      const file = e.target.files[0];
      await this.$store.dispatch(FILE_UPLOAD, file);
      if (this.getFileUpload.status === 'success') {
        this.form.files.push(this.getFileUpload.item);
      }
    },
    async onDeletePhoto(index) {
      const file = this.form.files[index];
      if (file) {
        await this.$store.dispatch(FILE_DELETE, file.id);
        this.form.files.splice(index, 1);
      }
    },
  },

  components: {
    ModalMain,
    InputString,
    GalleryPhotos,
  },
};
</script>
